/**
 * The **design tier** of a widget: variant, orientation and format controls —
 * everything about how a chart LOOKS, with no dataset involved.
 *
 * ## Why this file is tiny
 *
 * The DVM Layout Builder already describes all of this in
 * `dvm-layout-builder.json` + `charts/*.json`, and
 * `dvm-builder-config.utils.ts` already has pure selectors for it
 * (`applicableFormatControls`, `groupFormatControls`, `capabilitiesForType`).
 * The Design Builder reuses **the config and those selectors**, not the Layout
 * Builder's step components — those inject `DvmBuilderHost`, an abstract class
 * with ~100 members, which would have to be reimplemented here.
 *
 * So all that is actually missing is a per-layoutType note saying *which*
 * options key holds that family's variant list. That is this file: data, not
 * logic. A new chart type needs one row here (or none, if it has no variants)
 * and it gets a full design panel for free.
 */

import type {
  BuilderLayoutConfig,
  FormatControl,
  LabelValueOption,
} from '../../shared/self-service-config.types';
import { ZONE_SLIDER_DEFAULTS } from '../../../global-components/zone-slider/zone-slider.utils';
import { BUTTON_DEFAULTS } from '../../../global-components/dvm-action-button/dvm-action-button.types';

/** Per-family design-tier description. */
export interface AppearanceSpec {
  /** Key in `layout.options` holding this family's variant list. */
  variantOptionsKey?: string;
  /** Label for the variant control. */
  variantLabel?: string;
  /**
   * Capability field the chosen variant is written to. Most families call it
   * `variant`; Table & Matrix calls it `tableType`. Without this the choice
   * would be stored but silently ignored by the component.
   */
  variantCapabilityKey?: string;
  /** Family supports a vertical/horizontal flip. */
  orientation?: boolean;
  /** Defaults applied to a freshly dropped widget. */
  defaults?: Record<string, unknown>;
  /**
   * Extra design-tier controls this family needs that the shared
   * `formatControls` registry does not cover — the input controls' label,
   * placeholder and range. Declared in the same `FormatControl` shape so
   * `DesignControlRendererComponent` renders them with no special casing.
   */
  controls?: FormatControl[];
}

/**
 * Only families with a design-tier choice appear here. KPI, waterfall and the
 * input controls have no variant, so they simply fall through to the shared
 * format controls.
 */
export const APPEARANCE_SPECS: Record<string, AppearanceSpec> = {
  BAR: {
    variantOptionsKey: 'barVariants',
    variantLabel: 'Variant',
    orientation: true,
    defaults: { variant: 'grouped', orientation: 'vertical' },
  },
  LINE_AREA: {
    variantOptionsKey: 'lineAreaVariants',
    variantLabel: 'Variant',
    defaults: { variant: 'line' },
  },
  SCATTER_BUBBLE: {
    variantOptionsKey: 'scatterBubbleVariants',
    variantLabel: 'Variant',
    defaults: { variant: 'bubble' },
  },
  PIE_COMPOSITION: {
    variantOptionsKey: 'pieCompositionVariants',
    variantLabel: 'Variant',
    defaults: { variant: 'pie' },
  },
  TABLE_MATRIX: {
    variantOptionsKey: 'tableTypes',
    variantLabel: 'Table type',
    variantCapabilityKey: 'tableType',
    defaults: { variant: 'normal' },
  },

  // Input controls have no variant, but they DO have design-tier settings —
  // the label and placeholder you want to write while laying the screen out,
  // long before any dataset is wired up.
  DROPDOWN: {
    defaults: { label: 'Region', placeholder: 'Select region', multiple: false },
    controls: [
      { key: 'label', label: 'Label', control: 'text' },
      { key: 'placeholder', label: 'Placeholder', control: 'text' },
      { key: 'multiple', label: 'Allow multiple', control: 'toggle', default: false },
      // Follow the resized box by default; disable fill to use a fixed height.
      { key: 'fieldHeight', label: 'Field height (px, 0 = default)', control: 'number', default: 0, min: 0, max: 200 },
      { key: 'fieldFillHeight', label: 'Field fills box height', control: 'toggle', default: true },
    ],
  },
  SLIDER: {
    defaults: { label: 'Discount %', min: 0, max: 100, step: 1 },
    controls: [
      { key: 'label', label: 'Label', control: 'text' },
      { key: 'min', label: 'Minimum', control: 'number', default: 0 },
      { key: 'max', label: 'Maximum', control: 'number', default: 100 },
      { key: 'step', label: 'Step', control: 'number', default: 1, min: 0.01 },
    ],
  },
  // Every setting is design tier (it is a control, not a chart); the inspector
  // renders them through its own ZoneSliderSettingsComponent (grouped, with a
  // zone-list editor), so no flat `controls` list here. The data tier —
  // dataset-driven value / range / zones — lives in `widget.binding`, edited in
  // the Data tab (ZoneSliderDataComponent).
  ZONE_SLIDER: {
    defaults: { ...ZONE_SLIDER_DEFAULTS, zones: ZONE_SLIDER_DEFAULTS.zones.map((zone) => ({ ...zone })) },
  },
  // One feature per button, picked in the inspector's own ButtonSettingsComponent
  // (feature dropdown + that feature's settings), so no flat `controls` here.
  BUTTON: {
    defaults: { ...BUTTON_DEFAULTS, apis: [], apiTargets: [], filterTargets: [] },
  },
  INPUT_BOX: {
    defaults: {
      label: 'Target margin',
      placeholder: 'Enter a value',
      labelPosition: 'top',
      // No label / field style defaults: the box follows the theme like a
      // dropdown's until one is picked in Format.
      stepper: false,
      step: 1,
    },
    controls: [
      { key: 'label', label: 'Label', control: 'text' },
      { key: 'placeholder', label: 'Placeholder', control: 'text' },
      { key: 'labelPosition', label: 'Label position', control: 'select', optionsKey: 'inputLabelPositions', default: 'top' },
      // Label size / colour / font and the field's colours, radius and font
      // live in Format → Formatting, the same controls (and keys) as a dropdown.
      // Increase / decrease arrows inside the box (app-input-box reads
      // `stepper` / `step` / `min` / `max` straight off its data).
      // Off = the user can type; on = the box only shows its value.
      { key: 'readOnly', label: 'Read only (no typing)', control: 'toggle', default: false },
      // "Fill from data" boxes: with every filter cleared, show "-" instead of
      // the whole dataset's value. On unless switched off.
      { key: 'emptyWithoutFilter', label: 'Empty when no filter is picked', control: 'toggle', default: true },
      // The one filter column this box depends on: "-" unless it has a value,
      // whatever the other filters hold. Blank = the toggle above decides.
      { key: 'requiredFilterField', label: 'Show only when this filter is picked (column)', control: 'text', placeholder: 'e.g. customer_spec' },
      { key: 'stepper', label: 'Increase / decrease buttons', control: 'toggle', default: false },
      // Only meaningful with the buttons on, so shown only then.
      { key: 'step', label: 'Step per click', control: 'number', default: 1, min: 0.0001, step: 0.1, visibleWhen: { key: 'stepper', values: [true] } },
      { key: 'min', label: 'Minimum (blank = none)', control: 'text', placeholder: 'e.g. 0', visibleWhen: { key: 'stepper', values: [true] } },
      { key: 'max', label: 'Maximum (blank = none)', control: 'text', placeholder: 'e.g. 100', visibleWhen: { key: 'stepper', values: [true] } },
    ],
  },
  // Data shape + theme. Column-level edits live in the inspector's Columns
  // section (they are per field, so they cannot be a flat control list).
  // A Table type like the others (Table & Matrix's list), so the same picker
  // shows "Nested Table" here and switches back to the other types.
  NESTED_TABLE: {
    variantOptionsKey: 'tableTypes',
    variantLabel: 'Table type',
    defaults: { variant: 'nested', levelCount: 3, rowCount: 4, childRowCount: 3 },
    controls: [
      { key: 'title', label: 'Title', control: 'text' },
      { key: 'subtitle', label: 'Subtitle', control: 'text' },
      { key: 'levelCount', label: 'Levels', control: 'number', default: 3, min: 1, max: 3 },
      { key: 'rowCount', label: 'Top-level rows', control: 'number', default: 4, min: 1, max: 10 },
      { key: 'childRowCount', label: 'Rows per expansion', control: 'number', default: 3, min: 1, max: 6 },
      { key: 'headerBackground', label: 'Header background', control: 'color', default: '#1f2a44' },
      { key: 'headerColor', label: 'Header text', control: 'color', default: '#ffffff' },
      { key: 'expandedRowBackground', label: 'Expanded row', control: 'color', default: '#e9eef7' },
      { key: 'panelBackground', label: 'Nested panel', control: 'color', default: '#f1f4f9' },
      { key: 'accentColor', label: 'Accent', control: 'color', default: '#4a8fe7' },
      { key: 'fontSize', label: 'Font size', control: 'number', default: 13, min: 10, max: 20 },
    ],
  },
  FILE_UPLOAD: {
    defaults: { buttonLabel: 'Upload file', accept: '.xlsx,.xls,.csv' },
    controls: [
      { key: 'buttonLabel', label: 'Button label', control: 'text' },
      { key: 'accept', label: 'Accepted types', control: 'text' },
    ],
  },
};

/**
 * Option lists for the spec controls above that the shared builder config does
 * not carry. Merged under the config's own `options` by the inspector.
 */
export const APPEARANCE_OPTIONS: Record<string, LabelValueOption[]> = {
  inputLabelPositions: [
    { label: 'Top', value: 'top' },
    { label: 'Left', value: 'left' },
    { label: 'Right', value: 'right' },
    { label: 'Bottom', value: 'bottom' },
  ],
};

/** Families whose spec controls cover every setting — no shared Format list. */
export const NO_FORMAT_LAYOUTS: ReadonlySet<string> = new Set(['ZONE_SLIDER', 'BUTTON']);

export const ORIENTATION_OPTIONS: LabelValueOption[] = [
  { label: 'Vertical (columns)', value: 'vertical' },
  { label: 'Horizontal (bars)', value: 'horizontal' },
];

export function appearanceSpecFor(layoutType: string): AppearanceSpec {
  return APPEARANCE_SPECS[layoutType] ?? {};
}

/** Default appearance for a freshly dropped widget of this type. */
export function defaultAppearance(layoutType: string): Record<string, unknown> {
  return { ...(appearanceSpecFor(layoutType).defaults ?? {}) };
}

/**
 * The variant list for a layout type, read from the merged builder config.
 * Returns `[]` when the family has no variants, so callers can just check
 * `.length` to decide whether to render the control.
 */
export function variantOptionsFor(
  layout: BuilderLayoutConfig | null,
  layoutType: string,
): VariantOption[] {
  const key = appearanceSpecFor(layoutType).variantOptionsKey;
  if (!layout || !key) return [];
  const raw = (layout.options as Record<string, unknown> | undefined)?.[key];
  return Array.isArray(raw) ? (raw as VariantOption[]) : [];
}

/**
 * A variant entry. Beyond label/value these carry the **semantics** the sample
 * data generator needs — `stack`, `usesSeries`, `needsTwoMeasures` — which is
 * what lets a stacked bar actually render stacked before any data is bound.
 */
export interface VariantOption extends LabelValueOption {
  stack?: boolean;
  normalize100?: boolean;
  usesSeries?: boolean;
  seriesFromMeasures?: boolean;
  needsTwoMeasures?: boolean;
  /** Pie & Composition: the variant needs a Parent / outer-group column. */
  usesParent?: boolean;
  /** Pie & Composition: a single-value progress meter. */
  gauge?: boolean;
  hint?: string;
}

export function variantByValue(
  layout: BuilderLayoutConfig | null,
  layoutType: string,
  value: unknown,
): VariantOption | null {
  return variantOptionsFor(layout, layoutType).find((option) => option.value === value) ?? null;
}

/** The chosen variant's value, falling back to the family's default. */
export function currentVariant(
  appearance: Record<string, unknown> | undefined,
  layoutType: string,
): string {
  const chosen = appearance?.['variant'];
  if (typeof chosen === 'string' && chosen) return chosen;
  return String(appearanceSpecFor(layoutType).defaults?.['variant'] ?? '');
}

export function currentOrientation(appearance: Record<string, unknown> | undefined): string {
  const chosen = appearance?.['orientation'];
  return chosen === 'horizontal' ? 'horizontal' : 'vertical';
}
